import React, { useState } from 'react';
import { QualificationCreatePayload } from '../types';
import { COLORS } from '../../../../core/constants/colors';
import { slugify } from '../../../../core/utils/slug';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: QualificationCreatePayload) => Promise<void>;
}

const INITIAL_STATE = { name: '', slug: '', description: '' };

export const AddQualificationModal: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [slugEdited, setSlugEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleNameChange = (name: string) => {
    setForm((prev) => ({
      ...prev,
      name,
      slug: slugEdited ? prev.slug : slugify(name),
    }));
  };

  const reset = () => {
    setForm(INITIAL_STATE);
    setSlugEdited(false);
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.slug.trim()) {
      setError('Name and slug are required');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || undefined,
      });
      reset();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create relationship');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity"
        onClick={handleClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-[60] p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
            <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>Add Relationship</h3>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-all"
              style={{ color: '#6B7280' }}
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
            {error && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="e.g., Strategic Partner"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>Slug *</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => {
                  setSlugEdited(true);
                  setForm({ ...form, slug: e.target.value });
                }}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="strategic-partner"
                pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
                title="Lowercase letters, numbers, and hyphens"
                required
              />
              <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                Lowercase, hyphenated. Auto-derived from name.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="Optional description"
                rows={3}
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium transition-all hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: COLORS.admin.primary }}
              >
                {isSubmitting ? 'Creating...' : 'Create Relationship'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
